import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, Sparkle, Fire, ArrowUpRight, ArrowsClockwise, CheckCircle, Info, Warning,
  Lightbulb, Rocket,
} from "@phosphor-icons/react";

const PRIORITY_META = {
  high:   { icon: Fire,     chip: "border-red-500/40 bg-red-500/10 text-red-300",       label: "Alta prioridad", dot: "bg-red-400" },
  medium: { icon: Warning,  chip: "border-amber-500/40 bg-amber-500/10 text-amber-300", label: "Prioridad media", dot: "bg-amber-400" },
  low:    { icon: Info,     chip: "border-[#C8A24A]/40 bg-[#C8A24A]/10 text-[#C8A24A]", label: "Sugerencia",       dot: "bg-[#C8A24A]" },
};

export function CommercialAdvisor({ compact = false, maxItems = null }) {
  const [data, setData] = useState({ total: 0, counts: { high: 0, medium: 0, low: 0 }, recommendations: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get("/atlas/advisor");
      setData(data);
    } catch (err) {
      setError(formatApiError(err, "ATLAS no está disponible por ahora"));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const items = maxItems ? data.recommendations.slice(0, maxItems) : data.recommendations;

  if (loading) return (
    <div className="rounded-2xl border border-[#C8A24A]/20 bg-[#1A1A1A] p-5 animate-pulse" data-testid="advisor-loading">
      <div className="h-4 w-40 bg-[#0A0A0A] rounded mb-3" />
      <div className="h-16 bg-[#0A0A0A] rounded" />
    </div>
  );
  if (error) return (
    <div className="rounded-2xl border border-red-500/30 bg-red-500/5 p-4" data-testid="advisor-error">
      <p className="text-sm text-red-300">{error}</p>
    </div>
  );

  if (compact) {
    return (
      <div className="rounded-2xl border border-[#C8A24A]/30 bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] p-4 sm:p-5" data-testid="advisor-widget">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-[#C8A24A]" />
            <p className="text-xs uppercase tracking-widest text-[#C8A24A]/70 font-semibold">ATLAS · Recomienda</p>
          </div>
          <Link to="/dashboard/advisor" className="text-xs font-semibold text-[#C8A24A] hover:underline inline-flex items-center gap-1" data-testid="advisor-widget-more">
            Ver todas <ArrowUpRight size={11} />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-4" data-testid="advisor-widget-clean">
            <CheckCircle size={20} className="inline text-emerald-400 mr-1.5 mb-0.5" />
            <span className="text-sm text-[#C8A24A]/80">Todo en orden. Buen trabajo.</span>
          </div>
        ) : (
          <ul className="space-y-2">
            {items.map((r) => <RecItem key={r.id} rec={r} compact />)}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-12" data-testid="advisor-page">
      <Link to="/dashboard" className="text-xs text-[#C8A24A]/70 hover:text-[#C8A24A] inline-flex items-center gap-1" data-testid="advisor-back-link">
        <ArrowLeft size={12} /> Panel
      </Link>
      <div className="mt-4 mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A] font-semibold inline-flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> ATLAS · Director Comercial
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl mt-2 tracking-tight" data-testid="advisor-title">
            Tus recomendaciones
          </h1>
          <p className="text-sm text-[#C8A24A]/70 mt-2 max-w-xl">
            Acciones concretas para mejorar tu rendimiento comercial. ATLAS recomienda; tú decides.
          </p>
        </div>
        <div className="flex gap-2">
          <div className="flex gap-1.5" data-testid="advisor-counts">
            {["high", "medium", "low"].map(lvl => {
              const m = PRIORITY_META[lvl];
              return (
                <div key={lvl} className={`px-2.5 py-1.5 rounded-lg border ${m.chip} text-xs uppercase tracking-widest font-bold`} data-testid={`advisor-count-${lvl}`}>
                  <span>{m.label}</span>
                  <span className="ml-1.5 font-mono">{data.counts[lvl]}</span>
                </div>
              );
            })}
          </div>
          <Button variant="outline" onClick={load} className="h-10" data-testid="advisor-refresh-btn">
            <ArrowsClockwise size={14} />
          </Button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-500/30 bg-emerald-500/5 p-16 text-center" data-testid="advisor-empty">
          <Rocket size={40} className="mx-auto text-emerald-400 mb-3" />
          <p className="font-heading text-2xl">¡Todo en orden!</p>
          <p className="text-sm text-[#C8A24A]/70 mt-2">
            No detecté oportunidades urgentes. Sigue publicando y respondiendo a tus leads.
          </p>
        </div>
      ) : (
        <ul className="space-y-3" data-testid="advisor-list">
          {items.map((r) => <RecItem key={r.id} rec={r} />)}
        </ul>
      )}
    </div>
  );
}

function RecItem({ rec, compact = false }) {
  const m = PRIORITY_META[rec.priority] || PRIORITY_META.low;
  const Icon = m.icon;
  return (
    <li className={`rounded-xl border ${m.chip} p-3 sm:p-4`} data-testid={`advisor-rec-${rec.id}`}>
      <div className="flex items-start gap-3">
        <div className="w-8 h-8 rounded-lg bg-black/30 flex items-center justify-center flex-shrink-0">
          <Icon size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs uppercase tracking-widest font-bold opacity-80">{m.label}</span>
            <span className="text-xs uppercase tracking-widest opacity-50">· {rec.category.replace(/_/g, " ")}</span>
          </div>
          <p className={`${compact ? "text-xs" : "text-sm"} font-semibold mt-1`} data-testid={`advisor-motive-${rec.id}`}>{rec.motive}</p>
          {!compact && (
            <div className="mt-2 space-y-1.5 text-xs opacity-90">
              <p className="flex items-start gap-1.5"><Lightbulb size={11} weight="fill" className="mt-0.5 flex-shrink-0" /><span><b>Acción:</b> {rec.action}</span></p>
              <p className="flex items-start gap-1.5"><Sparkle size={11} weight="fill" className="mt-0.5 flex-shrink-0" /><span><b>Beneficio:</b> {rec.benefit}</span></p>
            </div>
          )}
          <div className="mt-3">
            <Link
              to={rec.cta_href}
              data-testid={`advisor-cta-${rec.id}`}
              className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-md bg-current/10 border border-current/30 hover:bg-current/20"
            >
              {rec.cta_label} <ArrowUpRight size={11} />
            </Link>
          </div>
        </div>
      </div>
    </li>
  );
}
